import React from 'react';
import { Dropdown } from 'react-bootstrap';

// Komponen & Tipe
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { UserV4 } from '../schemas/UserV4Schema'; // Tipe data dari skema V4

/**
 * Props untuk komponen UsersTableV4.
 * @param users - Array objek pengguna V4 yang akan ditampilkan.
 * @param onUpdate - Callback yang dipanggil saat tombol 'Edit' diklik, menerima objek UserV4.
 * @param onDelete - Callback yang dipanggil saat tombol 'Remove' diklik, menerima ID pengguna.
 * @param onAssignProperty - Callback untuk fitur 'Assign Property', menerima objek UserV4.
 * @param onViewProperties - Callback untuk melihat properti pengguna, menerima objek UserV4.
 * @param onAssignRole - Callback untuk fitur 'Assign Role', menerima objek UserV4.
 */
interface UsersTableV4Props {
    users: UserV4[];
    onUpdate: (user: UserV4) => void;
    onDelete: (id: string) => void;
    onAssignProperty: (user: UserV4) => void;
    onViewProperties: (user: UserV4) => void;
    onAssignRole: (user: UserV4) => void;
}

/**
 * Komponen tabel untuk menampilkan daftar pengguna (Versi 4 Final).
 * Komponen ini bersifat presentasional dan menerima data serta handler dari parent.
 */
const UsersTableV4: React.FC<UsersTableV4Props> = ({ users, onUpdate, onDelete, onAssignProperty, onViewProperties, onAssignRole }) => {

    /**
     * Helper function untuk merender daftar grup sebagai string yang dipisahkan koma.
     * @param groups - Array objek grup dari pengguna.
     * @returns String berisi nama grup atau "No Groups".
     */
    const renderGroups = (groups: UserV4['groups']): string => {
        if (!groups || groups.length === 0) {
            return 'No Groups';
        }
        const groupNames = groups.map(g => g.name);
        if (groupNames.length > 2) {
            return `${groupNames.slice(0, 2).join(', ')}, and ${groupNames.length - 2} more`;
        }
        return groupNames.join(', ');
    };

    return (
        <div className="table-responsive">
            <table className="table table-hover table-vcenter">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Properties</th>
                        <th>Groups</th>
                        <th>Created At</th>
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="text-center text-muted py-4">
                                No users found matching your criteria.
                            </td>
                        </tr>
                    ) : (
                        users.map((user) => (
                            <tr key={user.id}>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.phone || '-'}</td>
                                <td>
                                    {/* *** PERUBAHAN UTAMA DI SINI *** */}
                                    {/* Kolom ini sekarang menjadi tombol yang menampilkan jumlah dan memicu onViewProperties */}
                                    <button
                                        className="btn btn-link p-0 text-decoration-none"
                                        onClick={() => onViewProperties(user)}
                                        title="Click to view assigned properties"
                                    >
                                        {user.properties?.length || 0} assigned
                                    </button>
                                </td>
                                <td title={user.groups?.map(g => g.name).join(', ')}>
                                    {renderGroups(user.groups)}
                                </td>
                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                <td className="text-end">
                                    {user.email !== 'dev@gotrasoft.com' && (
                                        <Dropdown>
                                            <Dropdown.Toggle variant="light" size="sm" id={`dropdown-user-${user.id}`}>
                                                Actions
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
                                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-update']} asComponent>
                                                    <Dropdown.Item onClick={() => onUpdate(user)}>
                                                        Edit
                                                    </Dropdown.Item>
                                                </RequireAuth>

                                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-assign-property']} asComponent>
                                                    <Dropdown.Item onClick={() => onAssignProperty(user)}>
                                                        Assign Property
                                                    </Dropdown.Item>
                                                </RequireAuth>

                                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-assign-role']} asComponent>
                                                    <Dropdown.Item onClick={() => onAssignRole(user)}>
                                                        Assign Role
                                                    </Dropdown.Item>
                                                </RequireAuth>

                                                <Dropdown.Divider />

                                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-delete']} asComponent>
                                                    <Dropdown.Item
                                                        className="text-danger"
                                                        onClick={() => onDelete(user.id)}
                                                    >
                                                        Remove
                                                    </Dropdown.Item>
                                                </RequireAuth>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default UsersTableV4;